import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import SEO from "@/components/storefront/SEO";
import JsonLd, { faqSchema, breadcrumbSchema } from "@/components/storefront/JsonLd";

const FAQ = [
  {
    q: "What does \"Coming Soon\" mean on products?",
    a: "Coming Soon items are inbound to our warehouse. Every listing is real and priced — join the waitlist on any product page and we'll email you the moment it's in stock and ready to ship. No charge, no obligation.",
  },
  {
    q: "How long does shipping take?",
    a: "Once items are in stock, orders are processed within 1–3 business days and shipped via USPS, UPS, or FedEx with full tracking. Most US orders arrive within a week of shipping. Free standard shipping on orders over $75.",
  },
  {
    q: "What's your return policy?",
    a: "30 days from delivery for unused items in original packaging. A few niche exceptions apply for everyone's safety: cut fabric/yardage, digital patterns, and opened needle or blade packs are final sale. Email us with your order number to start a return — full details on our Returns page.",
  },
  {
    q: "Do you sell wholesale?",
    a: "Yes — small-batch wholesale for quilt shops, sewing studios, longarm quilters, pattern designers, and independent makers. Apply on our Wholesale page or email wholesale@biscaynequiltsupply.com. We review every application personally and reply within 1–2 business days.",
  },
  {
    q: "Do you ship internationally?",
    a: "Not yet — we currently ship within the United States only, including Alaska, Hawaii, and US territories. International shipping is on our roadmap.",
  },
  {
    q: "Are the fat quarters true 18\" x 22\" cuts?",
    a: "Yes. Our fat quarter bundles are cut to a full 18\" x 22\" and pressed flat, so you lose nothing to squaring up. Care instructions and exact specs are listed on every product page.",
  },
  {
    q: "Can I change or cancel my order?",
    a: "If your order hasn't shipped, we can usually make changes. Email hello@biscaynequiltsupply.com with your order number as soon as possible and we'll do our best.",
  },
  {
    q: "How do I track my order?",
    a: "You'll receive a tracking link by email as soon as the carrier scans your package. If it hasn't arrived within 3 business days of your ship confirmation, check spam or just email us.",
  },
  {
    q: "Is checkout secure?",
    a: "Yes. Payments are processed over encrypted connections by our payment provider, and we never see or store full card details on our servers.",
  },
];

export default function FAQ_Page() {
  const [open, setOpen] = useState(FAQ[0]?.q ?? null);
  const [q, setQ] = useState("");
  const filtered = FAQ.filter((x) => !q || x.q.toLowerCase().includes(q.toLowerCase()) || x.a.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-3xl px-6 py-14 md:py-24" data-testid="faq-page">
      <SEO title="FAQ" description="Frequently asked questions about Biscayne Quilt Supply orders, shipping, returns and more." />
      <JsonLd data={faqSchema(FAQ)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ])} />
      <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Help center</div>
      <h1 className="font-display text-5xl leading-tight md:text-[80px]">Frequently <span className="font-display-soft">asked</span></h1>
      <p className="mt-5 text-[#6B5158]">A few answers to our most common questions. Still need help? <Link to="/contact" className="text-[#9D5C6C] underline decoration-[#9D5C6C] underline-offset-4">Contact us</Link>.</p>

      <div className="relative mt-10">
        <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#6B5158]" />
        <input
          placeholder="Search the FAQ..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-full border border-[#E6D4CC] bg-white py-3.5 pl-11 pr-4 text-sm outline-none focus:border-[#2B2127]"
          data-testid="faq-search"
        />
      </div>

      <div className="mt-8 divide-y divide-[#E6D4CC] border-y border-[#E6D4CC]">
        {filtered.map((item, i) => {
          const isOpen = open === item.q;
          return (
            <div key={item.q}>
              <button onClick={() => setOpen(isOpen ? null : item.q)} className="flex w-full items-center justify-between gap-4 py-6 text-left" data-testid={`faq-${i}`}>
                <span className="font-display text-lg text-[#2B2127]">{item.q}</span>
                <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && <p className="pb-6 text-sm leading-relaxed text-[#6B5158]">{item.a}</p>}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="py-12 text-center text-sm text-[#6B5158]">No matching question — try a different keyword or <Link to="/contact" className="text-[#9D5C6C] underline">ask us directly</Link>.</div>}
      </div>
    </div>
  );
}
